import { uuToSim } from '../config';
import type { Vec3 } from '../types';

export type GoalExplosionAnimationMode = 'once' | 'loop' | 'fit-duration';
export type GoalExplosionModelTransitionKind =
  | 'none'
  | 'scale'
  | 'fade'
  | 'from-above'
  | 'from-below'
  | 'from-goal'
  | 'from-field'
  | 'elastic-drop'
  | 'elastic-rise';

export interface GoalExplosionModelTransition {
  readonly kind: GoalExplosionModelTransitionKind;
  readonly duration: number;
  readonly distance: number;
}

export type GoalExplosionAnimationClipSelector =
  | Readonly<{ kind: 'all' }>
  | Readonly<{ kind: 'index'; index: number }>;

export interface GoalExplosionAnimationLayer {
  readonly id: string;
  readonly clip: GoalExplosionAnimationClipSelector;
  readonly clipStartSeconds: number;
  readonly clipEndSeconds: number | null;
  readonly startSeconds: number;
  readonly endSeconds: number | null;
  readonly mode: GoalExplosionAnimationMode;
  readonly timeScale: number;
  readonly weight: number;
}

export interface GoalExplosionModelVisual {
  readonly url: string;
  readonly sceneIndex: number;
  readonly scale: number;
  readonly sourceAnchor: Vec3;
  readonly rotationYRadians: number;
  readonly enterTransition: GoalExplosionModelTransition;
  readonly exitTransition: GoalExplosionModelTransition | null;
  readonly animations: readonly GoalExplosionAnimationLayer[];
}

export interface GoalExplosionVisualOffset {
  readonly vertical: number;
  readonly towardField: number;
}

export type GoalExplosionVfxPhase = 'entry' | 'sustain' | 'exit';
export type GoalExplosionVfxParticleShape = 'energy' | 'debris' | 'block';
export type GoalExplosionVfxParticleMotion = 'radial' | 'swirl' | 'float';
export type GoalExplosionVfxColorSource = 'team' | 'core' | 'white' | 'fixed';

export interface GoalExplosionVfxLayerBase {
  readonly id: string;
  readonly enabled: boolean;
  readonly phase: GoalExplosionVfxPhase;
  readonly offset: Vec3;
  readonly delaySeconds: number;
}

export interface GoalExplosionParticleBurstLayer extends GoalExplosionVfxLayerBase {
  readonly kind: 'particle-burst';
  readonly shape: GoalExplosionVfxParticleShape;
  readonly colorSource: GoalExplosionVfxColorSource;
  readonly color?: number;
  readonly particles: number;
  readonly spawnRadius: number;
  readonly minSpeed: number;
  readonly maxSpeed: number;
  readonly upwardBias: number;
  readonly minLifetime: number;
  readonly maxLifetime: number;
  readonly minStartScale: number;
  readonly maxStartScale: number;
  readonly endScale: number;
  readonly drag: number;
  readonly gravity: number;
}

export interface GoalExplosionParticleEmitterLayer extends GoalExplosionVfxLayerBase {
  readonly kind: 'particle-emitter';
  readonly shape: GoalExplosionVfxParticleShape;
  readonly motion: GoalExplosionVfxParticleMotion;
  readonly colorSource: GoalExplosionVfxColorSource;
  readonly color?: number;
  readonly particlesPerSecond: number;
  readonly durationSeconds: number | null;
  readonly radius: number;
  readonly height: number;
  readonly minSpeed: number;
  readonly maxSpeed: number;
  readonly minLifetime: number;
  readonly maxLifetime: number;
  readonly minStartScale: number;
  readonly maxStartScale: number;
  readonly endScale: number;
  readonly drag: number;
  readonly gravity: number;
}

export interface GoalExplosionRingLayer extends GoalExplosionVfxLayerBase {
  readonly kind: 'ring';
  readonly colorSource: GoalExplosionVfxColorSource;
  readonly color?: number;
  readonly count: number;
  readonly duration: number;
  readonly maxRadius: number;
  readonly opacity: number;
  readonly verticalSpacing: number;
}

export interface GoalExplosionAuraLayer extends GoalExplosionVfxLayerBase {
  readonly kind: 'aura';
  readonly colorSource: GoalExplosionVfxColorSource;
  readonly color?: number;
  readonly durationSeconds: number | null;
  readonly rings: number;
  readonly radius: number;
  readonly baseHeight: number;
  readonly opacity: number;
  readonly pulseAmount: number;
  readonly pulseSpeed: number;
  readonly wobbleSpeed: number;
}

export interface GoalExplosionLightPulseLayer extends GoalExplosionVfxLayerBase {
  readonly kind: 'light-pulse';
  readonly colorSource: GoalExplosionVfxColorSource;
  readonly color?: number;
  readonly duration: number;
  readonly intensity: number;
  readonly distance: number;
}

export type GoalExplosionVfxLayer =
  | GoalExplosionParticleBurstLayer
  | GoalExplosionParticleEmitterLayer
  | GoalExplosionRingLayer
  | GoalExplosionAuraLayer
  | GoalExplosionLightPulseLayer;

export interface GoalExplosionVfxDefinition {
  readonly version: 1;
  readonly entryDuration: number;
  readonly exitDuration: number;
  readonly layers: readonly GoalExplosionVfxLayer[];
}

export interface GoalExplosionVisual {
  readonly duration: number;
  readonly offset: GoalExplosionVisualOffset;
  readonly model: GoalExplosionModelVisual | null;
  readonly vfx: GoalExplosionVfxDefinition;
  readonly coreColor: number;
}

export interface GoalExplosionTransitionState {
  readonly scale: number;
  readonly opacity: number;
  readonly offset: Vec3;
}

const VFX_BUDGET = Object.freeze({
  layers: 32,
  burstParticlesPerLayer: 512,
  burstParticlesTotal: 1_024,
  particlesPerSecondPerLayer: 256,
  particlesPerSecondTotal: 512,
  auraRings: 6,
  ringCount: 8,
});

export function defaultGoalExplosionVfxDefinition(): GoalExplosionVfxDefinition {
  return {
    version: 1,
    entryDuration: 0.45,
    exitDuration: 0.45,
    layers: [
      {
        id: 'entry-energy', enabled: true, phase: 'entry', kind: 'particle-burst', delaySeconds: 0,
        offset: { x: 0, y: 0, z: 0 }, shape: 'energy', colorSource: 'team', particles: 58,
        spawnRadius: 1.72, minSpeed: 2.4, maxSpeed: 8.6, upwardBias: 1,
        minLifetime: 0.72, maxLifetime: 1.45, minStartScale: 0.16, maxStartScale: 0.52,
        endScale: 0.025, drag: 0.85, gravity: -0.35,
      },
      {
        id: 'entry-debris', enabled: true, phase: 'entry', kind: 'particle-burst', delaySeconds: 0,
        offset: { x: 0, y: 0, z: 0 }, shape: 'debris', colorSource: 'fixed', color: 0x718086,
        particles: 38, spawnRadius: 5.8, minSpeed: 1.4, maxSpeed: 6.2, upwardBias: 3.2,
        minLifetime: 0.9, maxLifetime: 1.85, minStartScale: 0.22, maxStartScale: 0.72,
        endScale: 0.08, drag: 0.9, gravity: -5.6,
      },
      {
        id: 'sustain-energy', enabled: true, phase: 'sustain', kind: 'particle-emitter', delaySeconds: 0,
        offset: { x: 0, y: 0, z: 0 }, shape: 'energy', motion: 'swirl', colorSource: 'team',
        particlesPerSecond: 52, durationSeconds: null, radius: 7.8, height: 8.5,
        minSpeed: 0.55, maxSpeed: 1.85, minLifetime: 1.15, maxLifetime: 2.25,
        minStartScale: 0.09, maxStartScale: 0.17, endScale: 0.018, drag: 0.18, gravity: 0.04,
      },
      {
        id: 'sustain-debris', enabled: true, phase: 'sustain', kind: 'particle-emitter', delaySeconds: 0,
        offset: { x: 0, y: 0, z: 0 }, shape: 'debris', motion: 'radial', colorSource: 'fixed', color: 0x718086,
        particlesPerSecond: 3.5, durationSeconds: null, radius: 5.8, height: 3.2,
        minSpeed: 0.48, maxSpeed: 2.1, minLifetime: 1.08, maxLifetime: 2.22,
        minStartScale: 0.16, maxStartScale: 0.52, endScale: 0.08, drag: 0.65, gravity: -1.68,
      },
      {
        id: 'portal-aura', enabled: true, phase: 'sustain', kind: 'aura', delaySeconds: 0,
        offset: { x: 0, y: 0, z: 0 }, colorSource: 'team', durationSeconds: null, rings: 3,
        radius: 5.9, baseHeight: 0.45, opacity: 0.42, pulseAmount: 0.08, pulseSpeed: 1.7, wobbleSpeed: 0.65,
      },
      {
        id: 'entry-ring-team', enabled: true, phase: 'entry', kind: 'ring', delaySeconds: 0,
        offset: { x: 0, y: 0, z: 0 }, colorSource: 'team', count: 1, duration: 0.82,
        maxRadius: uuToSim(1450), opacity: 0.56, verticalSpacing: 0,
      },
      {
        id: 'entry-ring-core', enabled: true, phase: 'entry', kind: 'ring', delaySeconds: 0.08,
        offset: { x: 0, y: 0, z: 0 }, colorSource: 'core', count: 1, duration: 0.56,
        maxRadius: uuToSim(783), opacity: 0.5, verticalSpacing: 0,
      },
    ],
  };
}

export function whiteTntBurstLayer(
  id: string,
  phase: GoalExplosionVfxPhase,
): GoalExplosionParticleBurstLayer {
  return {
    id, enabled: true, phase, kind: 'particle-burst', delaySeconds: 0,
    offset: { x: 0, y: 0, z: 0 }, shape: 'block', colorSource: 'white', particles: 96,
    spawnRadius: 1.2, minSpeed: 4.5, maxSpeed: 11.5, upwardBias: 1.2,
    minLifetime: 0.55, maxLifetime: 1.15, minStartScale: 0.12, maxStartScale: 0.3,
    endScale: 0.02, drag: 0.75, gravity: -1.4,
  };
}

export function whiteSuspendedEmitterLayer(id = 'white-suspended'): GoalExplosionParticleEmitterLayer {
  return {
    id, enabled: true, phase: 'sustain', kind: 'particle-emitter', delaySeconds: 0,
    offset: { x: 0, y: 0, z: 0 }, shape: 'energy', motion: 'float', colorSource: 'white',
    particlesPerSecond: 28, durationSeconds: null, radius: 5.2, height: 8,
    minSpeed: 0.08, maxSpeed: 0.42, minLifetime: 1.6, maxLifetime: 3.1,
    minStartScale: 0.08, maxStartScale: 0.2, endScale: 0.025, drag: 0.12, gravity: 0,
  };
}

export function validateGoalExplosionVisual(visual: GoalExplosionVisual): void {
  positive(visual.duration, 'goal explosion duration');
  finite(visual.offset.vertical, 'goal explosion vertical offset');
  nonNegative(visual.offset.towardField, 'goal explosion toward-field offset');
  color(visual.coreColor, 'goal explosion core color');
  if (visual.model) validateGoalExplosionModel(visual.model, visual.duration);
  validateGoalExplosionVfxDefinition(visual.vfx, visual.duration);
}

export function validateGoalExplosionVfxDefinition(value: GoalExplosionVfxDefinition, effectDuration?: number): void {
  if (value.version !== 1) throw new Error('Unsupported goal explosion VFX version.');
  nonNegative(value.entryDuration, 'goal VFX entry duration');
  nonNegative(value.exitDuration, 'goal VFX exit duration');
  if (effectDuration !== undefined && value.entryDuration + value.exitDuration > effectDuration) {
    throw new Error('Goal VFX entry and exit windows cannot exceed the effect duration.');
  }
  if (!Array.isArray(value.layers) || value.layers.length > VFX_BUDGET.layers) {
    throw new Error(`Goal VFX supports at most ${VFX_BUDGET.layers} layers.`);
  }
  const ids = new Set<string>();
  let burstParticles = 0;
  let emissionRate = 0;
  for (const layer of value.layers) {
    if (!layer.id.trim() || layer.id.length > 64 || ids.has(layer.id)) {
      throw new Error('Goal VFX layer IDs must be unique, non-empty, and at most 64 characters.');
    }
    ids.add(layer.id);
    if (typeof layer.enabled !== 'boolean') throw new Error(`Goal VFX layer ${layer.id} enabled must be a boolean.`);
    if (!['entry', 'sustain', 'exit'].includes(layer.phase)) throw new Error(`Goal VFX layer ${layer.id} phase is invalid.`);
    finiteVec3(layer.offset, `goal VFX layer ${layer.id} offset`);
    nonNegative(layer.delaySeconds, `goal VFX layer ${layer.id} delay`);
    if (layer.kind === 'particle-burst') {
      validateParticleShape(layer.shape, layer.id);
      validateColorSource(layer.colorSource, layer.color, layer.id);
      positiveInteger(layer.particles, `goal VFX layer ${layer.id} particle count`);
      if (layer.particles > VFX_BUDGET.burstParticlesPerLayer) throw new Error(`Goal VFX layer ${layer.id} exceeds the burst particle budget.`);
      burstParticles += layer.particles;
      validateParticlePhysics(layer, layer.id);
    } else if (layer.kind === 'particle-emitter') {
      validateParticleShape(layer.shape, layer.id);
      if (!['radial', 'swirl', 'float'].includes(layer.motion)) throw new Error(`Goal VFX layer ${layer.id} motion is invalid.`);
      validateColorSource(layer.colorSource, layer.color, layer.id);
      nonNegative(layer.particlesPerSecond, `goal VFX layer ${layer.id} particle rate`);
      if (layer.particlesPerSecond > VFX_BUDGET.particlesPerSecondPerLayer) {
        throw new Error(`Goal VFX layer ${layer.id} exceeds the emitter rate budget.`);
      }
      emissionRate += layer.particlesPerSecond;
      if (layer.durationSeconds !== null) positive(layer.durationSeconds, `goal VFX layer ${layer.id} duration`);
      positive(layer.radius, `goal VFX layer ${layer.id} radius`);
      nonNegative(layer.height, `goal VFX layer ${layer.id} height`);
      validateParticlePhysics(layer, layer.id);
    } else if (layer.kind === 'ring') {
      validateColorSource(layer.colorSource, layer.color, layer.id);
      positiveInteger(layer.count, `goal VFX layer ${layer.id} ring count`);
      if (layer.count > VFX_BUDGET.ringCount) throw new Error(`Goal VFX layer ${layer.id} exceeds the ring count budget.`);
      positive(layer.duration, `goal VFX layer ${layer.id} ring duration`);
      positive(layer.maxRadius, `goal VFX layer ${layer.id} ring radius`);
      unit(layer.opacity, `goal VFX layer ${layer.id} ring opacity`);
      finite(layer.verticalSpacing, `goal VFX layer ${layer.id} ring spacing`);
    } else if (layer.kind === 'aura') {
      validateColorSource(layer.colorSource, layer.color, layer.id);
      if (layer.durationSeconds !== null) positive(layer.durationSeconds, `goal VFX layer ${layer.id} duration`);
      positiveInteger(layer.rings, `goal VFX layer ${layer.id} aura rings`);
      if (layer.rings > VFX_BUDGET.auraRings) throw new Error(`Goal VFX layer ${layer.id} exceeds the aura ring budget.`);
      positive(layer.radius, `goal VFX layer ${layer.id} aura radius`);
      finite(layer.baseHeight, `goal VFX layer ${layer.id} aura base height`);
      unit(layer.opacity, `goal VFX layer ${layer.id} aura opacity`);
      unit(layer.pulseAmount, `goal VFX layer ${layer.id} aura pulse amount`);
      positive(layer.pulseSpeed, `goal VFX layer ${layer.id} aura pulse speed`);
      nonNegative(layer.wobbleSpeed, `goal VFX layer ${layer.id} aura wobble speed`);
    } else if (layer.kind === 'light-pulse') {
      validateColorSource(layer.colorSource, layer.color, layer.id);
      positive(layer.duration, `goal VFX layer ${layer.id} light duration`);
      nonNegative(layer.intensity, `goal VFX layer ${layer.id} light intensity`);
      positive(layer.distance, `goal VFX layer ${layer.id} light distance`);
    }
  }
  if (burstParticles > VFX_BUDGET.burstParticlesTotal) throw new Error('Goal VFX exceeds the total burst particle budget.');
  if (emissionRate > VFX_BUDGET.particlesPerSecondTotal) throw new Error('Goal VFX exceeds the total emitter rate budget.');
}

export function goalExplosionAnimationLayerTime(
  layer: GoalExplosionAnimationLayer,
  clipDurationSeconds: number,
  effectDurationSeconds: number,
  ageSeconds: number,
): number | null {
  if (!Number.isFinite(clipDurationSeconds) || clipDurationSeconds <= 0) return 0;
  const end = layer.endSeconds ?? effectDurationSeconds;
  if (ageSeconds < layer.startSeconds || ageSeconds > end || end <= layer.startSeconds) return null;
  const sourceStart = Math.min(layer.clipStartSeconds, clipDurationSeconds);
  const sourceEnd = Math.min(layer.clipEndSeconds ?? clipDurationSeconds, clipDurationSeconds);
  const sourceDuration = Math.max(0, sourceEnd - sourceStart);
  if (sourceDuration <= 0) return sourceStart;
  const localAge = ageSeconds - layer.startSeconds;
  const windowDuration = end - layer.startSeconds;
  const scale = layer.mode === 'fit-duration'
    ? Math.min(1, layer.timeScale, sourceDuration / windowDuration)
    : layer.timeScale;
  const time = localAge * scale;
  if (layer.mode === 'loop') return sourceStart + positiveModulo(time, sourceDuration);
  return Math.min(sourceStart + time, sourceEnd);
}

export function goalExplosionModelTransitionState(
  model: Pick<GoalExplosionModelVisual, 'enterTransition' | 'exitTransition'>,
  effectDurationSeconds: number,
  ageSeconds: number,
  towardFieldSign: number,
): GoalExplosionTransitionState {
  if (ageSeconds < model.enterTransition.duration) {
    return transitionState(model.enterTransition, clamp01(ageSeconds / model.enterTransition.duration), true, towardFieldSign);
  }
  const exit = model.exitTransition;
  if (exit && ageSeconds > effectDurationSeconds - exit.duration) {
    const progress = clamp01((ageSeconds - (effectDurationSeconds - exit.duration)) / exit.duration);
    return transitionState(exit, progress, false, towardFieldSign);
  }
  return { scale: 1, opacity: 1, offset: { x: 0, y: 0, z: 0 } };
}

export function phaseStartSeconds(
  phase: GoalExplosionVfxPhase,
  definition: GoalExplosionVfxDefinition,
  effectDuration: number,
): number {
  if (phase === 'exit') return Math.max(0, effectDuration - definition.exitDuration);
  return 0;
}

export function phaseDurationSeconds(
  phase: GoalExplosionVfxPhase,
  definition: GoalExplosionVfxDefinition,
  effectDuration: number,
): number {
  if (phase === 'entry') return definition.entryDuration;
  if (phase === 'exit') return definition.exitDuration;
  return effectDuration;
}

function validateGoalExplosionModel(model: GoalExplosionModelVisual, totalDuration: number): void {
  if (!model.url.trim()) throw new Error('Goal explosion model URL is required.');
  nonNegativeInteger(model.sceneIndex, 'goal explosion model scene index');
  positive(model.scale, 'goal explosion model scale');
  finiteVec3(model.sourceAnchor, 'goal explosion model source anchor');
  finite(model.rotationYRadians, 'goal explosion model rotation');
  validateTransition(model.enterTransition, 'goal explosion model entry transition');
  if (model.exitTransition) validateTransition(model.exitTransition, 'goal explosion model exit transition');
  if (model.enterTransition.duration + (model.exitTransition?.duration ?? 0) > totalDuration) {
    throw new Error('Goal explosion model entry and exit transitions cannot exceed the effect duration.');
  }
  if (!Array.isArray(model.animations) || model.animations.length > 16) {
    throw new Error('Goal explosion model supports at most 16 animation layers.');
  }
  const ids = new Set<string>();
  for (const layer of model.animations) {
    if (!layer.id.trim() || layer.id.length > 64 || ids.has(layer.id)) {
      throw new Error('Goal explosion animation layer IDs must be unique, non-empty, and at most 64 characters.');
    }
    ids.add(layer.id);
    if (layer.clip.kind === 'index') nonNegativeInteger(layer.clip.index, `goal explosion animation ${layer.id} clip index`);
    else if (layer.clip.kind !== 'all') throw new Error(`Goal explosion animation ${layer.id} clip selector is invalid.`);
    nonNegative(layer.clipStartSeconds, `goal explosion animation ${layer.id} clip start`);
    if (layer.clipEndSeconds !== null) {
      positive(layer.clipEndSeconds, `goal explosion animation ${layer.id} clip end`);
      if (layer.clipEndSeconds <= layer.clipStartSeconds) {
        throw new Error(`Goal explosion animation ${layer.id} clip end must be after clip start.`);
      }
    }
    nonNegative(layer.startSeconds, `goal explosion animation ${layer.id} start`);
    if (layer.endSeconds !== null) {
      positive(layer.endSeconds, `goal explosion animation ${layer.id} end`);
      if (layer.endSeconds <= layer.startSeconds || layer.endSeconds > totalDuration) {
        throw new Error(`Goal explosion animation ${layer.id} end must be after start and within the effect.`);
      }
    }
    if (!['once', 'loop', 'fit-duration'].includes(layer.mode)) throw new Error(`Goal explosion animation ${layer.id} mode is invalid.`);
    positive(layer.timeScale, `goal explosion animation ${layer.id} time scale`);
    unit(layer.weight, `goal explosion animation ${layer.id} weight`);
  }
}

function transitionState(
  transition: GoalExplosionModelTransition,
  progress: number,
  entering: boolean,
  towardFieldSign: number,
): GoalExplosionTransitionState {
  const visibleProgress = entering ? progress : 1 - progress;
  const eased = transition.kind.startsWith('elastic') ? elasticOut(visibleProgress) : smoothstep01(visibleProgress);
  const hidden = 1 - eased;
  if (transition.kind === 'none') return { scale: 1, opacity: 1, offset: { x: 0, y: 0, z: 0 } };
  if (transition.kind === 'fade') return { scale: 1, opacity: eased, offset: { x: 0, y: 0, z: 0 } };
  if (transition.kind === 'scale') return { scale: Math.max(0.04, eased), opacity: 1, offset: { x: 0, y: 0, z: 0 } };
  const distance = transition.distance * hidden;
  if (transition.kind === 'from-above' || transition.kind === 'elastic-drop') {
    return { scale: 1, opacity: 1, offset: { x: 0, y: distance, z: 0 } };
  }
  if (transition.kind === 'from-below' || transition.kind === 'elastic-rise') {
    return { scale: 1, opacity: 1, offset: { x: 0, y: -distance, z: 0 } };
  }
  const zDirection = transition.kind === 'from-goal' ? towardFieldSign : -towardFieldSign;
  return { scale: 1, opacity: 1, offset: { x: 0, y: 0, z: zDirection * distance } };
}

function validateTransition(transition: GoalExplosionModelTransition, label: string): void {
  if (!['none', 'scale', 'fade', 'from-above', 'from-below', 'from-goal', 'from-field', 'elastic-drop', 'elastic-rise'].includes(transition.kind)) {
    throw new Error(`${label} kind is invalid.`);
  }
  if (transition.kind === 'none') nonNegative(transition.duration, `${label} duration`);
  else positive(transition.duration, `${label} duration`);
  nonNegative(transition.distance, `${label} distance`);
}

function validateParticlePhysics(
  layer: Pick<GoalExplosionParticleBurstLayer,
    'spawnRadius' | 'minSpeed' | 'maxSpeed' | 'upwardBias' | 'minLifetime' | 'maxLifetime'
    | 'minStartScale' | 'maxStartScale' | 'endScale' | 'drag' | 'gravity'>
    | Pick<GoalExplosionParticleEmitterLayer,
      'radius' | 'minSpeed' | 'maxSpeed' | 'minLifetime' | 'maxLifetime'
      | 'minStartScale' | 'maxStartScale' | 'endScale' | 'drag' | 'gravity'>,
  id: string,
): void {
  const radius = 'spawnRadius' in layer ? layer.spawnRadius : layer.radius;
  nonNegative(radius, `goal VFX layer ${id} spawn radius`);
  nonNegative(layer.minSpeed, `goal VFX layer ${id} minimum speed`);
  nonNegative(layer.maxSpeed, `goal VFX layer ${id} maximum speed`);
  ordered(layer.minSpeed, layer.maxSpeed, `goal VFX layer ${id} speed range`);
  if ('upwardBias' in layer) finite(layer.upwardBias, `goal VFX layer ${id} upward bias`);
  positive(layer.minLifetime, `goal VFX layer ${id} minimum lifetime`);
  positive(layer.maxLifetime, `goal VFX layer ${id} maximum lifetime`);
  ordered(layer.minLifetime, layer.maxLifetime, `goal VFX layer ${id} lifetime range`);
  positive(layer.minStartScale, `goal VFX layer ${id} minimum start scale`);
  positive(layer.maxStartScale, `goal VFX layer ${id} maximum start scale`);
  ordered(layer.minStartScale, layer.maxStartScale, `goal VFX layer ${id} start scale range`);
  nonNegative(layer.endScale, `goal VFX layer ${id} end scale`);
  nonNegative(layer.drag, `goal VFX layer ${id} drag`);
  finite(layer.gravity, `goal VFX layer ${id} gravity`);
}

function validateParticleShape(value: GoalExplosionVfxParticleShape, id: string): void {
  if (!['energy', 'debris', 'block'].includes(value)) throw new Error(`Goal VFX layer ${id} particle shape is invalid.`);
}

function validateColorSource(source: GoalExplosionVfxColorSource, fixedColor: number | undefined, id: string): void {
  if (!['team', 'core', 'white', 'fixed'].includes(source)) throw new Error(`Goal VFX layer ${id} color source is invalid.`);
  if (source === 'fixed') color(fixedColor, `goal VFX layer ${id} fixed color`);
}

function elasticOut(value: number): number {
  if (value <= 0 || value >= 1) return value;
  return 2 ** (-10 * value) * Math.sin((value * 10 - 0.75) * (2 * Math.PI / 3)) + 1;
}

function positiveModulo(value: number, modulus: number): number {
  if (modulus <= 0) return 0;
  return ((value % modulus) + modulus) % modulus;
}

function clamp01(value: number): number { return Math.min(1, Math.max(0, value)); }
function smoothstep01(value: number): number { return value * value * (3 - 2 * value); }
function positiveInteger(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value <= 0) throw new Error(`${label} must be a positive integer.`);
}
function nonNegativeInteger(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error(`${label} must be a non-negative integer.`);
}
function positive(value: number, label: string): void {
  if (!Number.isFinite(value) || value <= 0) throw new Error(`${label} must be a positive finite number.`);
}
function nonNegative(value: number, label: string): void {
  if (!Number.isFinite(value) || value < 0) throw new Error(`${label} must be a non-negative finite number.`);
}
function finite(value: number, label: string): void {
  if (!Number.isFinite(value)) throw new Error(`${label} must be finite.`);
}
function ordered(minimum: number, maximum: number, label: string): void {
  if (maximum < minimum) throw new Error(`${label} maximum must be greater than or equal to minimum.`);
}
function unit(value: number, label: string): void {
  if (!Number.isFinite(value) || value < 0 || value > 1) throw new Error(`${label} must be between 0 and 1.`);
}
function color(value: number | undefined, label: string): void {
  if (!Number.isInteger(value) || value! < 0 || value! > 0xffffff) throw new Error(`${label} must be an RGB integer.`);
}
function finiteVec3(value: Vec3, label: string): void {
  finite(value.x, `${label} x`);
  finite(value.y, `${label} y`);
  finite(value.z, `${label} z`);
}
